import { TableCell, TableRow } from '@/components/ui/table';
import { PriceTrendChartCell } from './PriceTrendChartCell';
import {
  AggregatedRowData,
  UnaggregatedRowData,
  SupplierRowData,
  isAggregatedRowData,
  isSupplierRowData,
  formatCurrency,
  safeArrayAccess,
} from './utils';
import { useQuoteSheetTableContext } from './quotes-sheet-table.context';
import { cn } from '@/lib/utils';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

// Re-export the constants for consistency with QuoteSheetTable
const AGGREGATE_KEY_SEPARATOR = ' / ';
const SUPPLIER_KEY_PREFIX = 'supplier';

const styles = (canExpand: boolean | undefined, depth: number) => {
  const baseStyles = cn('border-stone-300');
  if (!canExpand) {
    return cn(baseStyles, 'bg-background');
  }

  if (depth === 0) {
    return cn(baseStyles, 'bg-stone-200');
  }

  return cn(baseStyles, 'bg-stone-100');
};

function AggregatedOrUnaggregatedRow({
  data,
  aggregateKeyPrefix = [],
}: {
  data: AggregatedRowData | UnaggregatedRowData;
  aggregateKeyPrefix: string[];
}) {
  const children = data.children;
  const { showHistoricalData, expandedAggregates, toggleAggregate } =
    useQuoteSheetTableContext();

  const { chartData, historicalData } = useMemo(() => {
    if (isAggregatedRowData(data)) {
      return {
        chartData: data.aggregatedPricesByDate?.map((price) => ({
          date: price.date,
          price: price.priceFOBAvg,
        })),
        historicalData: data.aggregatedPricesByDate
          ?.slice(0, 3)
          .map(({ date, priceFOBAvg }) => ({
            date,
            price: priceFOBAvg,
          })),
      };
    }

    return {
      chartData: data.pricesByDate?.map((price) => ({
        date: price.date,
        price: price.priceFOB,
      })),
      historicalData: data.pricesByDate
        ?.slice(0, 3)
        .map(({ date, priceFOB }) => ({
          date,
          price: priceFOB,
        })),
    };
  }, [data]);

  const aggregateKey = [...aggregateKeyPrefix, `${data.label}-${data.key}`];
  const aggregateKeyString = aggregateKey.join(AGGREGATE_KEY_SEPARATOR);

  const canExpand = children && children.length > 0;

  return (
    <>
      <TableRow key={data.key} className={styles(canExpand, data.depth)}>
        <TableCell
          onClick={() =>
            canExpand ? toggleAggregate(aggregateKeyString) : undefined
          }
          className={cn(canExpand && 'cursor-pointer')}
          style={{
            paddingLeft: `${(data.depth + 0.5) * 16}px`,
          }}
        >
          <div className="flex items-center">
            {canExpand && (
              <>
                {expandedAggregates.includes(aggregateKeyString) ? (
                  <CaretDownIcon className="h-4 w-4 mr-2" />
                ) : (
                  <CaretRightIcon className="h-4 w-4 mr-2" />
                )}
              </>
            )}
            <span className="font-medium">{data.label}</span>
            {canExpand && (
              <span className="text-xs text-muted-foreground ml-1">
                ({children.length})
              </span>
            )}
          </div>
        </TableCell>
        {/* Price trend chart hidden per GEN-830 */}
        {/* {chartData ? (
          <PriceTrendChartCell
            data={chartData}
            className="border-l border-stone-300"
          />
        ) : (
          <TableCell className="border-l border-stone-300" />
        )} */}
        {showHistoricalData && (
          <>
            <TableCell className="text-right font-mono">
              {formatCurrency(safeArrayAccess(historicalData, 0)?.price)}
            </TableCell>
            <TableCell className="text-right font-mono">
              {formatCurrency(
                safeArrayAccess(
                  historicalData,
                  (historicalData?.length || 0) - 3,
                )?.price,
              )}
            </TableCell>
            <TableCell className="text-right font-mono">
              {formatCurrency(
                safeArrayAccess(
                  historicalData,
                  (historicalData?.length || 0) - 2,
                )?.price,
              )}
            </TableCell>
          </>
        )}
        <TableCell className="border-l border-stone-300">
          <div className="font-bold text-lg font-mono text-right">
            {formatCurrency(
              safeArrayAccess(chartData, (chartData?.length || 0) - 1)?.price,
            )}
          </div>
        </TableCell>
      </TableRow>
      {expandedAggregates.includes(aggregateKeyString) &&
        children &&
        children.map((child) => (
          <QuoteSheetDashboardRow
            key={child.key}
            data={child}
            aggregateKeyPrefix={aggregateKey}
          />
        ))}
    </>
  );
}

function SupplierRow({
  data,
  aggregateKeyPrefix = [],
}: {
  data: SupplierRowData;
  aggregateKeyPrefix: string[];
}) {
  const navigate = useNavigate();
  const { showHistoricalData, expandedAggregates, toggleAggregate } =
    useQuoteSheetTableContext();
  const lastPrice = safeArrayAccess(
    data.pricesByDate,
    (data.pricesByDate?.length || 0) - 1,
  );

  const aggregateKey = [
    ...aggregateKeyPrefix,
    `${SUPPLIER_KEY_PREFIX}-${data.key}`,
  ];
  const aggregateKeyString = aggregateKey.join(AGGREGATE_KEY_SEPARATOR);

  const canExpand = data.children && data.children.length > 0;

  return (
    <>
      <TableRow className={styles(canExpand, data.depth)}>
        <TableCell
          onClick={() =>
            canExpand ? toggleAggregate(aggregateKeyString) : undefined
          }
          className={cn(canExpand && 'cursor-pointer')}
          style={{
            paddingLeft: `${(data.depth + 0.5) * 16}px`,
          }}
        >
          <div className="flex items-center">
            {canExpand && (
              <>
                {expandedAggregates.includes(aggregateKeyString) ? (
                  <CaretDownIcon className="h-4 w-4 mr-2" />
                ) : (
                  <CaretRightIcon className="h-4 w-4 mr-2" />
                )}
              </>
            )}
            <span className="font-medium">{data.vendor.name}</span>
            {canExpand && (
              <span className="text-xs text-muted-foreground ml-1">
                ({data.children?.length ?? 0})
              </span>
            )}
            {/* {getTrendIcon(lastPrice.pctChange)} */}
          </div>
        </TableCell>
        {/* Price trend chart hidden per GEN-830 */}
        {/* {data.pricesByDate && !canExpand ? (
          <>
            <PriceTrendChartCell
              className="border-l border-stone-300"
              data={data.pricesByDate.map((price) => ({
                date: price.date,
                price: price.priceFOB,
              }))}
            />
          </>
        ) : (
          <TableCell className="border-l border-stone-300" />
        )} */}
        {showHistoricalData && (
          <>
            <TableCell className="text-right font-mono">
              <div className="font-medium">
                {formatCurrency(
                  safeArrayAccess(data.pricesByDate, 0)?.priceFOB,
                )}
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">
              <div className="font-medium">
                {formatCurrency(
                  safeArrayAccess(
                    data.pricesByDate,
                    (data.pricesByDate?.length || 0) - 3,
                  )?.priceFOB,
                )}
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">
              <div className="font-medium">
                {formatCurrency(
                  safeArrayAccess(
                    data.pricesByDate,
                    (data.pricesByDate?.length || 0) - 2,
                  )?.priceFOB,
                )}
              </div>
            </TableCell>
          </>
        )}
        <TableCell
          className={cn(
            'text-right border-l border-stone-300 font-mono',
            lastPrice?.priceQuoteSheetId && 'cursor-pointer hover:bg-muted',
          )}
          onClick={() => {
            if (lastPrice?.priceQuoteSheetId) {
              navigate({
                to: '/price-sheet-dashboard/detail/$quoteSheetId',
                params: { quoteSheetId: lastPrice.priceQuoteSheetId },
              });
            }
          }}
        >
          <div className="flex gap-1 justify-end">
            <span
              className={cn(
                'text-lg',
                canExpand && 'text-md text-muted-foreground',
              )}
            >
              {canExpand
                ? 'View Latest Price Sheet'
                : formatCurrency(lastPrice?.priceFOB)}
            </span>
            {/* TODO rethinking this - what is the best thing to show here? */}
            {/*<div
              className={`text-sm flex items-center justify-end ${getTrendColor(
                lastPrice?.pctChange,
              )}`}
            >
               {lastPrice.pctChange
              ? lastPrice.pctChange > 0
                ? `+${lastPrice.pctChange.toFixed(1)}%`
                : `${lastPrice.pctChange.toFixed(1)}%`
              : ''}
            </div> */}
          </div>
        </TableCell>
      </TableRow>
      {expandedAggregates.includes(aggregateKeyString) &&
        data.children &&
        data.children.map((child) => (
          <QuoteSheetDashboardRow
            key={child.key}
            data={child}
            aggregateKeyPrefix={aggregateKey}
          />
        ))}
    </>
  );
}

export function QuoteSheetDashboardRow({
  data,
  aggregateKeyPrefix,
}: {
  data: AggregatedRowData | UnaggregatedRowData | SupplierRowData;
  aggregateKeyPrefix: string[];
}) {
  if (isSupplierRowData(data)) {
    return <SupplierRow data={data} aggregateKeyPrefix={aggregateKeyPrefix} />;
  }

  return (
    <AggregatedOrUnaggregatedRow
      data={data}
      aggregateKeyPrefix={aggregateKeyPrefix}
    />
  );
}
